import { type RailIcon } from "./rail-tile";
import type { Omittable } from "@salestrak/ui";

/** Default width of the module rail column (icon + label tiles). */
export const MODULE_RAIL_WIDTH = 66;
/** Rail width in compact mode — tiles render icon-only, no labels. */
export const MODULE_RAIL_WIDTH_COMPACT = 58;

export type RailTileData = Omittable<{
  id: string;
  icon: RailIcon;
  label: string;
  /** Workspace-relative route the tile navigates to (workspace prefix is
   * applied by `useSidebarNav`). */
  route: string;
  /** Static fallback badge. Live counts (e.g. Home's inbox count) are resolved
   * dynamically at render and take precedence — see `ModuleRail`. */
  badge?: string;
  /** False for modules without a module menu (the workspace layout resolves
   * their sidebar pane to null). Defaults to true. */
  hasSubmenu?: boolean;
}>;

// Static tile registry — the source of truth for each tile's presentation
// and routing, in default order. The user's `railTiles` preference reorders
// and hides these (resolved in `useNavPreferences`); the rail renders the
// resolved list from `ModuleRailContext`, never this array directly.
export const TILES: RailTileData[] = [
  { id: "home", icon: "home", label: "Home", route: "/" },
  { id: "map", icon: "map-pin", label: "Map", route: "/map", hasSubmenu: false },
  { id: "workflows", icon: "workflow", label: "Workflows", route: "/workflows" },
  { id: "teams", icon: "users", label: "Teams", route: "/teams" },
];

/**
 * Resolve which tile owns a route, given the route segment that follows
 * `[workspace]` (from `useSegments`). The active tile is DERIVED from the
 * route — it survives reloads and back/forward navigation for free.
 *
 * Module ownership is broader than the tiles' entry `route`s: a module owns
 * every screen reachable from its module menu (e.g. Home owns `/inbox`, Teams
 * owns `/members`). Settings routes belong to no module (they have their own
 * dedicated sidebar) — null clears the tile highlight.
 */
export function tileIdForRouteSegment(
  segment: string | undefined,
): string | null {
  switch (segment) {
    case undefined: // workspace index — Home's "My Work"
    case "index":
    case "inbox":
    case "my-work":
      return "home";
    case "map":
      return "map";
    case "workflows": // the workflows roster (/workflows)
    case "workflow": // a single workflow's views (/workflow/[id])
      return "workflows";
    case "teams":
    case "team":
    case "members":
    // All-Members route segment is the singular `member`; member views share the
    // Teams sidebar (decision #5).
    case "member":
      return "teams";
    default:
      // settings and anything unrecognized: no module owns it.
      return null;
  }
}